import intersect from '@turf/intersect';
import area from '@turf/area';
import { featureCollection } from '@turf/helpers';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import type { AreaFeature, AreaGeoJSON } from './types';
import { isPointInFeature, getFeatureBoundingArea } from './pointInPolygon';

/**
 * Minimum fraction of the place's area that the intersection must cover for a
 * district to be considered as truly overlapping (vs. a shared-border sliver
 * caused by floating-point coordinate noise).
 */
const MIN_OVERLAP_RATIO = 0.001; // 0.1% of the place's area

/**
 * Finds the place feature whose polygon contains the given point.
 * Returns null if no city polygon contains the point (e.g., unincorporated area).
 */
export function findPlaceAtPoint(lat: number, lng: number, citiesGeoJson: AreaGeoJSON): AreaFeature | null {
    const point = { lat, lng };

    let bestFeature: AreaFeature | null = null;
    let bestArea = Infinity;

    for (const feature of citiesGeoJson.features) {
        if (isPointInFeature(point, feature)) {
            const a = getFeatureBoundingArea(feature);
            if (a < bestArea) {
                bestArea = a;
                bestFeature = feature;
            }
        }
    }
    return bestFeature ?? null;
}

/**
 * Returns the district numbers whose polygon shares meaningful (non-sliver) area
 * with the given place polygon. Shared-edge-only touches are excluded by requiring
 * the intersection area to exceed a small fraction of the place's area; this
 * prevents floating-point noise along shared borders from producing false positives.
 *
 * Order matches the order of features in districtsGeoJson.
 */
export function findDistrictsIntersectingPlace(
    placeFeature: AreaFeature,
    districtsGeoJson: AreaGeoJSON
): string[] {
    const placeGeom = placeFeature.geometry as Polygon | MultiPolygon;
    const placeArea = area(placeGeom);
    const minIntersectionArea = placeArea * MIN_OVERLAP_RATIO;
    const matched: string[] = [];

    for (const districtFeature of districtsGeoJson.features) {
        const districtGeom = districtFeature.geometry as Polygon | MultiPolygon;

        const intersection = intersect(
            featureCollection([
                { type: 'Feature', properties: {}, geometry: districtGeom } as Feature<Polygon | MultiPolygon>,
                { type: 'Feature', properties: {}, geometry: placeGeom } as Feature<Polygon | MultiPolygon>,
            ])
        );

        if (intersection && area(intersection) > minIntersectionArea) {
            matched.push(districtFeature.properties.Name);
        }
    }

    return matched;
}
